import { useEffect, useState } from 'react';

import Toolbar from '@lcsc-ui/components/ToolBar';
import BaseLayout from '@lcsc-ui/layouts/baseLayout';
import useUserStore from '@lcsc-ui/store/user';
import ecpCurl from '@lcsc-ui/utils/ecpCurl';
import Utils from '@lcsc-ui/utils/Utils';
import Head from 'next/head';
import { NextSeo } from 'next-seo';

import MroApi from '@/apis/mro';
import OpApi from '@/apis/op';
import StaticdataApi from '@/apis/staticdata';
import MroCatalogContent from '@/features/mro/MroCatalogContent';

const metadata = {
  title: 'MRO工业品_MRO工业品线上采购商城-立创商城',
  additionalMetaTags: [
    {
      name: 'keywords',
      content: 'MRO,工业品,MRO工业品,MRO采购,工业品商城,工业品采购网,立创商城',
    },
    {
      name: 'description',
      content:
        '立创商城-MRO工业品一站式采购平台，提供工具、量具、防护用品、工控电气、办公用品耗材和标准件等工业用品线上采购服务。工业品网上超市就选立创MRO商城。',
    },
  ],
};

const CatalogPage = ({
  resourceData,
  websiteSectionList,
  catalogList,
  headerAd,
  catalogData,
  notIndexHeaderAdapter,
}) => {
  const { updateCommonConfigVO, updateProjectConfigVO } = useUserStore();
  const [isOldCus, setIsOldCus] = useState(false);
  const [isLgScreen, setIsLgScreen] = useState(null);
  const [catalogCount, setCatalogCount] = useState('');
  const [catalogListData, setCatalogListData] = useState(null);

  useEffect(() => {
    fetchConfig();
    handleResize();
    setIsOldCus(resourceData?.commonConfigVO?.existSendOrder || false);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    hanleDataFn(catalogData);
  }, [catalogData]);

  const hanleDataFn = (catalog) => {
    let list = [];
    for (const key in catalog) {
      if (Object.prototype.hasOwnProperty.call(catalog, key)) {
        list = list.concat(...catalog[key]);
      }
    }

    const catalogLevelOneOrder = [
      '电容',
      '电阻',
      '连接器',
      '二极管',
      '电源管理',
      '处理器及微控制器',
      '晶体管',
      '开关',
      '光电器件',
      '电感/线圈/变压器',
      '晶体/振荡器/谐振器',
      '数据转换芯片',
      '接口芯片',
      '射频无线电',
      '放大器、线性器件',
      '嵌入式外围芯片',
      '逻辑芯片',
      '开发板/开发工具',
      '存储器',
      '传感器',
      '滤波器/EMI优化',
      '保险丝',
      '显示模块/LED驱动/显示驱动',
      '仪器仪表/工具',
      '继电器',
      '五金/焊材/辅料/电池',
      '功能模块',
      '物联网/通信模块',
      '音频器件/微电机(马达)',
      '劳动保护用品',
      '实用电子电器/书籍',
      '免费赠品区',
    ];
    let count = 0;

    list.forEach((item) => {
      item.isSearch = true;
      item.catalogLevelCount = item.groupProductCount;
      if (item.sonCatalogList) {
        item.sonCatalogList.forEach((sonCatalog) => {
          sonCatalog.isSearch = true;
          if (sonCatalog.sonCatalogList) {
            sonCatalog.sonCatalogList.forEach((grandsonCatalog) => {
              grandsonCatalog.isSearch = true;
            });
          }
        });
      }
      count += Number(item.catalogLevelCount);
    });

    let result = [];
    catalogLevelOneOrder.forEach((catalogLevelOne) => {
      // 将一级目录按照给定的顺序排序
      const ci = list.findIndex((l) => l.catalogName === catalogLevelOne);
      if (ci !== -1) {
        result.push(list[ci]);
        list.splice(ci, 1);
      }
    });
    if (list.length) {
      result = result.concat(...list);
    }
    setCatalogCount(count);
    setCatalogListData(result);
  };

  const fetchConfig = () => {
    updateProjectConfigVO(resourceData?.projectConfigVO);
    updateCommonConfigVO(resourceData?.commonConfigVO);
  };

  const handleResize = () => {
    setIsLgScreen(Utils.isLgScreen());
  };

  return (
    <>
      <NextSeo title={metadata.title} additionalMetaTags={metadata.additionalMetaTags} />
      <Head>
        <link rel="canonical" href={resourceData?.projectConfigVO?.lcscWwwUrl + '/mro.html'} />
      </Head>
      <BaseLayout
        resourceData={resourceData}
        websiteSectionList={websiteSectionList}
        catalogList={catalogList}
        headerAd={headerAd}
        isLgScreen={isLgScreen}
        notIndexHeaderAdapter={notIndexHeaderAdapter}
      >
        <section className="w-[1200px] _lg:w-[1552px] bg-[white] m-[20px_auto_0px] mb-[48px]">
          <MroCatalogContent
            resourceData={resourceData}
            catalogCount={catalogCount}
            catalogListData={catalogListData}
            catalogData={catalogData}
          />
        </section>
        <Toolbar isOldCus={isOldCus} />
      </BaseLayout>
    </>
  );
};

export default CatalogPage;

export async function getStaticPaths() {
  return {
    paths: [].map((item) => ({
      params: { id: item.toString() },
    })),
    fallback: 'blocking',
  };
}
export async function getStaticProps(context) {
  const { id } = context?.params || {
    id: '',
  };
  if (!id || id.replace('.html', '') !== 'index') {
    return {
      notFound: true,
      revalidate: 1,
    };
  }
  try {
    // 并行请求所有需要的数据
    const [resourceData, catalogList, websiteSectionList, headerAd, catalogData] = await Promise.all([
      ecpCurl(StaticdataApi.resourceList, { enableCache: true }),
      ecpCurl(StaticdataApi.catalogList),
      ecpCurl(OpApi.websiteSection, { enableCache: true }),
      ecpCurl(OpApi.advertisingHeader, { enableCache: true }),
      ecpCurl(MroApi.mroCatalog),
    ]);

    return {
      props: {
        resourceData,
        websiteSectionList,
        catalogList,
        headerAd,
        catalogData,
        notIndexHeaderAdapter: true,
      },
      // ISR 核心配置：每 6 小时重新验证一次页面
      // 当有请求进来时，如果距离上次生成超过 6 小时，Next.js 会在后台重新生成页面
      revalidate: 60 * 60 * 6,
    };
  } catch (error) {
    console.error('Error fetching catalog data:', error);

    // 即使出错也返回基本数据，避免页面完全失败
    return {
      props: {
        resourceData: null,
        websiteSectionList: null,
        catalogList: null,
        headerAd: null,
        catalogData: null,
        notIndexHeaderAdapter: true,
      },
      revalidate: 10, // 出错时更频繁地重试
    };
  }
}
